import type { UiState } from 'instantsearch.js';
import qs, { ParsedQs } from 'qs';

import { URL_ARRAY_SEPARATOR } from '../constants';

import type { DefaultFilters } from './getIndexName';
import { setItem } from './memoryStorage';

type Mapping = { [key: string]: string };

export function mapRefinementListToUiState(
  query: ParsedQs,
  mapping: Mapping,
  arraySeparator = URL_ARRAY_SEPARATOR
) {
  const refinementListState = Object.keys(query).reduce((acc, key) => {
    const stateKey = mapping[key];
    if (!stateKey) {
      return acc;
    }
    return {
      ...acc,
      [stateKey]: String(query[key]).split(arraySeparator),
    };
  }, {});
  if (Object.keys(refinementListState).length === 0) {
    return {};
  }
  return refinementListState;
}

export function mapMenuToUiState(query: ParsedQs, mapping: Mapping) {
  const menuState: Record<string, string> = Object.keys(query).reduce((acc, key) => {
    const stateKey = mapping[key];
    if (!stateKey) {
      return acc;
    }
    return {
      ...acc,
      [stateKey]: query[key],
    };
  }, {});

  if (Object.keys(menuState).length === 0) {
    return {};
  }

  return menuState;
}

export type RouteToStateAlgolia = {
  page?: string;
  query?: string;
  refinementList?: Mapping;
  menu?: Mapping;
  numericMenu?: Mapping;
  geoSearch?: string;
};

interface RouteToStateOptions {
  defaultFilters?: DefaultFilters;
  arraySeparator?: string;
}

function routeToState(
  queryString: string,
  indexName: string,
  {
    page: pageMapping = 'page',
    query: queryMapping = 'query',
    refinementList: refinementListMapping = {},
    menu: menuMapping = {},
    numericMenu: numericMenuMapping = {},
    geoSearch: geoSearchMapping = 'geoSearch',
  }: RouteToStateAlgolia,
  { defaultFilters = {}, arraySeparator = URL_ARRAY_SEPARATOR }: RouteToStateOptions = {}
): UiState {
  const routeState = qs.parse(queryString, { ignoreQueryPrefix: true });
  const refinementListKeys = Object.keys(refinementListMapping);
  const menuKeys = Object.keys(menuMapping);
  const numericMenuKeys = Object.keys(numericMenuMapping);
  const routeStateRest: Record<string, string> = Object.entries(routeState).reduce(
    (acc, [key, value]) => {
      if (
        refinementListKeys.includes(key) ||
        menuKeys.includes(key) ||
        numericMenuKeys.includes(key)
      ) {
        return acc;
      }
      return {
        ...acc,
        [key]: value,
      };
    },
    {}
  );
  const refinementList = mapRefinementListToUiState(
    routeState,
    refinementListMapping,
    arraySeparator
  );
  const menu = mapMenuToUiState(routeState, menuMapping);
  const numericMenu = mapMenuToUiState(routeState, numericMenuMapping);

  if (routeStateRest[pageMapping] && pageMapping !== 'page') {
    routeStateRest.page = routeStateRest[pageMapping];
    delete routeStateRest[pageMapping];
  }

  if (routeStateRest[queryMapping] && queryMapping !== 'query') {
    routeStateRest.query = routeStateRest[queryMapping];
    delete routeStateRest[queryMapping];
  }

  let geoSearch: { boundingBox: Array<number[]> } | undefined;

  if (routeStateRest[geoSearchMapping]) {
    const allLocations = routeStateRest[geoSearchMapping].split('__');

    geoSearch = { boundingBox: [] };

    allLocations.forEach((location) => {
      const [
        locationValue,
        locationLabel,
        locationOriginalValue,
        locationRadiusInKilometers,
        locationLabelWithRadius,
      ] = location.split('|');
      setItem(locationValue, {
        label: locationLabel,
        originalValue: locationOriginalValue,
        radiusInKilometers: Number(locationRadiusInKilometers),
        labelWithRadius: locationLabelWithRadius,
      });

      geoSearch!.boundingBox.push(locationValue.split(',').map(Number));
    });

    delete routeStateRest[geoSearchMapping];
  }

  let defaultGeoSearch: { boundingBox: Array<number[]> } | undefined;

  if (defaultFilters.geoSearch) {
    defaultGeoSearch = { boundingBox: [] };

    defaultFilters.geoSearch.forEach((location) => {
      const [
        locationValue,
        locationLabel,
        locationOriginalValue,
        locationRadiusInKilometers,
        locationLabelWithRadius,
      ] = location.split('|');
      setItem(locationValue, {
        label: locationLabel,
        originalValue: locationOriginalValue,
        radiusInKilometers: Number(locationRadiusInKilometers),
        labelWithRadius: locationLabelWithRadius,
      });
      defaultGeoSearch!.boundingBox.push(locationValue.split(',').map(Number));
    });
  }

  const hasSearchQuery = Boolean(routeStateRest.query);
  const isOnSpecificPage = Number(routeStateRest.page) > 0;
  const hasRefinedItems = Object.keys(refinementList).length > 0;
  const hasMenuItems = Object.keys(menu).length > 0;
  const hasNumericMenuItems = Object.keys(numericMenu).length > 0;
  const hasGeoSearch = Boolean(geoSearch);

  const shouldUseDefaultFilters =
    !hasSearchQuery &&
    !isOnSpecificPage &&
    !hasRefinedItems &&
    !hasMenuItems &&
    !hasNumericMenuItems &&
    !hasGeoSearch;

  const uiState = {
    [indexName]: {
      ...routeStateRest,
      refinementList: shouldUseDefaultFilters ? defaultFilters.refinementList : refinementList,
      menu: shouldUseDefaultFilters ? defaultFilters.menu : menu,
      numericMenu: shouldUseDefaultFilters ? defaultFilters.numericMenu : numericMenu,
      geoSearch: shouldUseDefaultFilters ? defaultGeoSearch : geoSearch,
    },
  };

  return uiState;
}
export default routeToState;
